import {
  useCallback, useEffect, useId, useMemo, useRef, useState,
  type KeyboardEvent, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredPosition } from '@/hooks/useAnchoredPosition';

export interface SelectOption {
  value: string;
  label: string;
  /** Right-aligned and dimmed, e.g. a row count. Never carries meaning alone. */
  hint?: string;
  disabled?: boolean;
}

interface SelectProps {
  /** Visible label. Always rendered — a bare dropdown makes the reader infer what it controls. */
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  /** Prepended "no filter" row. Its label should say what unfiltered MEANS. */
  placeholder?: string;
  /** Short clarifier under the control, e.g. where the values come from. */
  hint?: ReactNode;
  /** Label and control on one line, for dense toolbars. */
  inline?: boolean;
  disabled?: boolean;
  /** Defaults to on above 7 options — the point where scanning beats reading. */
  searchable?: boolean;
  searchPlaceholder?: string;
}

/**
 * The one dropdown in this app.
 *
 * <b>A port of SalesIQ's `Select`</b> (`client-engine/libs/shared-ui/src/components/Select/`), not
 * a native `<select>` and not shadcn. There is no shadcn or Radix anywhere in this monorepo, and
 * this app has no Tailwind — but its `styles.css` was copied from SalesIQ's and defines the same
 * `--primary` / `--border` / `--bg-surface` tokens, so the behaviour ports cleanly into plain CSS.
 *
 * <b>Why not keep the native control.</b> Its popup is drawn by the OS: system font, solid blue
 * highlight, no option hints, and no way to search. It also cannot render below the fold without
 * the browser's own scrolling, and it is clipped inside the `overflow-x: auto` table wrappers this
 * app already uses.
 *
 * <b>What a custom listbox must earn back</b>, because the native control gave it away free, and
 * all of it is implemented here: arrow / Home / End / Enter / Escape, `aria-activedescendant`,
 * `role="listbox"` with `aria-selected`, focus restored to the trigger on close, and a panel that
 * flips up rather than opening off-screen.
 */
export function Select({
  label, value, onChange, options, placeholder, hint, inline = false,
  disabled, searchable, searchPlaceholder,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const fieldId = useId();
  const listboxId = useId();
  const optionPrefix = useId();

  const isEmpty = options.length === 0;
  const showSearch = searchable ?? options.length > 7;

  // The placeholder is a real row, so "clear the filter" is reachable by keyboard like any option.
  const rows = useMemo<SelectOption[]>(
    () => (placeholder !== undefined ? [{ value: '', label: placeholder }, ...options] : options),
    [options, placeholder],
  );

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? rows.filter((o) => o.label.toLowerCase().includes(q)) : rows;
  }, [rows, filter]);

  const selected = useMemo(() => rows.find((o) => o.value === value), [rows, value]);
  const optionIdAt = (i: number) => `${optionPrefix}-${i}`;

  const position = useAnchoredPosition({ open, anchorRef: triggerRef, floatingRef: panelRef });

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    // Deferred: React must unmount the panel before focus moves, or focus lands on <body>.
    if (restoreFocus) queueMicrotask(() => triggerRef.current?.focus());
  }, []);

  const pick = useCallback((option: SelectOption | undefined) => {
    if (!option || option.disabled) return;
    onChange(option.value);
    close();
  }, [onChange, close]);

  // On open: clear the filter and point the active row at the CURRENT selection, so arrows start
  // from where the user already is rather than from the top of the list.
  useEffect(() => {
    if (!open) return;
    setFilter('');
    const index = rows.findIndex((o) => o.value === value);
    setActiveIndex(index >= 0 ? index : 0);
    queueMicrotask(() => {
      if (showSearch) searchRef.current?.focus();
      else listRef.current?.focus();
    });
  }, [open, showSearch, rows, value]);

  // Keep the active row inside the list as filtering shrinks it.
  useEffect(() => {
    if (activeIndex >= visible.length) setActiveIndex(Math.max(0, visible.length - 1));
  }, [visible.length, activeIndex]);

  // Scroll the active row into view — arrowing past the fold is otherwise invisible.
  useEffect(() => {
    if (!open || !position.ready) return;
    document.getElementById(optionIdAt(activeIndex))?.scrollIntoView({ block: 'nearest' });
  });

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      // No focus restore: the user is deliberately going somewhere else.
      close(false);
    };

    // Capture phase, so a handler that stops propagation cannot trap the panel open.
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open, close]);

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(visible.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActiveIndex(visible.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(visible[activeIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  const triggerLabel = selected?.label ?? placeholder ?? 'Select…';

  return (
    <div className={`field${inline ? ' field-inline' : ''}`}>
      <label className="field-label t-label" htmlFor={fieldId}>{label}</label>

      <button
        id={fieldId}
        ref={triggerRef}
        type="button"
        className="input select-trigger"
        disabled={disabled || isEmpty}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        onClick={() => setOpen((p) => !p)}
        onKeyDown={(e) => {
          // Opening with a down-arrow is the native behaviour people already expect.
          if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className={selected?.value ? 'select-value' : 'select-value is-placeholder'}>
          {triggerLabel}
        </span>
        <span className="select-chevron" aria-hidden>▾</span>
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className={`select-panel${position.ready ? '' : ' is-measuring'} is-${position.placement}`}
          style={position.style}
        >
          {showSearch && (
            <div className="select-search">
              <input
                ref={searchRef}
                type="text"
                className="select-search-input"
                value={filter}
                placeholder={searchPlaceholder ?? 'Search…'}
                onChange={(e) => setFilter(e.target.value)}
                onKeyDown={onKeyDown}
                aria-controls={listboxId}
                aria-activedescendant={visible.length ? optionIdAt(activeIndex) : undefined}
                autoComplete="off"
              />
            </div>
          )}

          <ul
            id={listboxId}
            ref={listRef}
            className="select-list cp-scroll"
            role="listbox"
            tabIndex={-1}
            aria-label={label}
            aria-activedescendant={visible.length ? optionIdAt(activeIndex) : undefined}
            onKeyDown={onKeyDown}
          >
            {visible.length === 0 && <li className="select-empty t-label subtle">No matches</li>}

            {visible.map((option, i) => (
              <li
                key={option.value || '(placeholder)'}
                id={optionIdAt(i)}
                role="option"
                aria-selected={option.value === value}
                aria-disabled={option.disabled || undefined}
                className={
                  'select-option'
                  + (i === activeIndex ? ' is-active' : '')
                  + (option.value === value ? ' is-selected' : '')
                  + (option.disabled ? ' is-disabled' : '')
                  + (option.value === '' ? ' is-clear' : '')
                }
                // Pointer-down, not click: a click fires after the trigger's blur, which would
                // already have closed the panel.
                onPointerDown={(e) => { e.preventDefault(); pick(option); }}
                onMouseEnter={() => setActiveIndex(i)}
              >
                <span className="select-option-label">{option.label}</span>
                {option.hint && <span className="select-option-hint t-caption subtle">{option.hint}</span>}
              </li>
            ))}
          </ul>
        </div>,
        document.body,
      )}

      {/* An empty dropdown says why. Silently disabled reads as broken. */}
      {isEmpty ? (
        <span className="field-hint t-caption subtle">No values in this range yet</span>
      ) : hint ? (
        <span className="field-hint t-caption subtle">{hint}</span>
      ) : null}
    </div>
  );
}

interface SegmentedProps<T extends string | number> {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  inline?: boolean;
}

/**
 * A small set of mutually exclusive choices, all visible at once.
 *
 * Used where a dropdown would hide the alternatives behind a click. Rule of thumb: four or fewer
 * options AND the reader benefits from seeing what else exists → this; otherwise {@link Select}.
 *
 * Selection is carried by background AND weight, never colour alone.
 */
export function Segmented<T extends string | number>({
  label, value, onChange, options, inline = false,
}: SegmentedProps<T>) {
  return (
    <div className={`field${inline ? ' field-inline' : ''}`}>
      {label && <span className="field-label t-label">{label}</span>}
      <div className="segmented" role="group" aria-label={label || undefined}>
        {options.map((o) => (
          <button
            key={String(o.value)}
            type="button"
            className={`segmented-item${o.value === value ? ' is-active' : ''}`}
            aria-pressed={o.value === value}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
